import type { WorkspaceArea } from "./adsAssistantPanelUtils";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

/**
 * Tab strip for the Sales-bucket Ads Assistant mode.
 *
 * Library was re-homed to the Manager-bucket `adsGuidance` mode on
 * 2026-06-15 (see docs/design/admin_split_and_entitlements.md §7.6).
 * Trainers / marketing / ads-guidance editors now open Manager →
 * Ads Guidance to edit categories / templates / policies / post
 * tags / social mappings. The remaining tabs here are the rep-side
 * surfaces (ingestion + suggestion) plus the trainer-side ops
 * config (feed import + org settings).
 */
export function AdsAssistantPanelAreaTabs(props: {
  canConfigureFeeds: boolean;
  workspaceArea: WorkspaceArea;
  setWorkspaceArea: (a: WorkspaceArea) => void;
}) {
  const { canConfigureFeeds, workspaceArea, setWorkspaceArea } = props;

  const tabCount = 2 + (canConfigureFeeds ? 2 : 0);
  const gridCols =
    tabCount === 4
      ? "md:grid-cols-4"
      : tabCount === 3
        ? "md:grid-cols-3"
        : "md:grid-cols-2";

  const btn = (area: WorkspaceArea, title: string, desc: string, fireflyTargetId?: string) => (
    <button
      type="button"
      onClick={() => setWorkspaceArea(area)}
      {...(fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: fireflyTargetId } : {})}
      className={
        "rounded-xl border bg-white p-4 text-left transition-colors hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 " +
        (workspaceArea === area
          ? "border-blue-300 shadow-[0_0_0_1px_rgba(59,130,246,0.2)] dark:border-blue-700"
          : "border-gray-200 dark:border-gray-600")
      }
    >
      <div className="font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{desc}</div>
    </button>
  );

  return (
    <div className={"grid grid-cols-1 gap-3 " + gridCols}>
      {btn("ingestion", "Ingestion", "Log ads with pasted copy, platform, and optional template.", "ads-ingest-tab")}
      {btn("suggestion", "Suggestion", "Browse indexed posts, stats, and coaching hints.", "ads-suggest-tab")}
      {canConfigureFeeds
        ? btn("feed_import", "Feed import", "Meta / Google fetch or paste JSON into the same index (trainer+).")
        : null}
      {canConfigureFeeds
        ? btn("org_settings", "Org Settings", "Platforms, retention, archive hint, suggestion defaults (trainer+).")
        : null}
    </div>
  );
}
